import axios from "axios";
import { reportNetworkError } from "./errorReporting";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });
api.interceptors.response.use((response) => response, reportNetworkError);
axios.interceptors.response.use((response) => response, reportNetworkError);

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("gsp_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const fileUrl = (id) => `${API}/files/${id}`;
export const vocRecUrl = (vocId) => `${API}/voc/recordings/${vocId}`;

const _filenameFromDisposition = (cd) => {
  if (!cd) return null;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  try { return m ? decodeURIComponent(m[1]) : null; } catch { return m ? m[1] : null; }
};

// Reliable file open for PDFs/images that works in installed PWAs (mobile + desktop),
// where a plain <a target="_blank"> to a PDF endpoint opens a blank window or a stuck
// download. We stream the bytes, wrap them in a blob: URL and hand that to the browser's
// native viewer (or trigger a real download).
export async function openFile(fileId, { download = false } = {}) {
  const win = download ? null : window.open("", "_blank");
  try {
    const res = await api.get(`/files/${fileId}`, {
      responseType: "blob",
      params: download ? { download: 1 } : undefined,
    });
    const type = res.data?.type || "application/octet-stream";
    const url = URL.createObjectURL(new Blob([res.data], { type }));
    const name = _filenameFromDisposition(res.headers?.["content-disposition"]) || "document";
    if (download) {
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
    } else if (win) {
      win.location.href = url;
    } else {
      window.location.href = url; // popup blocked → same tab
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (win) win.close();
    throw e;
  }
}

// Open/download a generated (non-stored) PDF from an authenticated API path (sends the
// bearer token, unlike a plain window.open), wrapping the bytes in a blob: URL.
export async function openUrl(path, { download = false, filename = "document.pdf" } = {}) {
  const win = download ? null : window.open("", "_blank");
  try {
    const res = await api.get(path, { responseType: "blob" });
    const type = res.data?.type || "application/pdf";
    const url = URL.createObjectURL(new Blob([res.data], { type }));
    const name = _filenameFromDisposition(res.headers?.["content-disposition"]) || filename;
    if (download) {
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
    } else if (win) {
      win.location.href = url;
    } else {
      window.location.href = url;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (win) win.close();
    throw e;
  }
}

// POST variant of openUrl: sends a JSON body, receives a generated PDF blob, and opens
// or downloads it. Used by the Job Pack combined-PDF download (needs selected document ids).
export async function postUrl(path, body = {}, { download = false, filename = "document.pdf" } = {}) {
  const win = download ? null : window.open("", "_blank");
  try {
    const res = await api.post(path, body, { responseType: "blob" });
    const type = res.data?.type || "application/pdf";
    const url = URL.createObjectURL(new Blob([res.data], { type }));
    const name = _filenameFromDisposition(res.headers?.["content-disposition"]) || filename;
    if (download) {
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
    } else if (win) {
      win.location.href = url;
    } else {
      window.location.href = url;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    if (win) win.close();
    throw e;
  }
}

export const errMsg = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((e) => e?.msg || (typeof e === "string" ? e : JSON.stringify(e))).join(", ");
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return err?.message || fallback;
};

export { API };
export default api;
